import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/nextjs'

import { fetchNearbySpaces } from 'features/space/services/nearbySpacesApi'
import type { FetchNearbySpacesParams, NearbySpaceListItem } from 'features/space/types/nearbySpace'

export const useNearbySpaces = () => {
  const [spaces, setSpaces] = useState<NearbySpaceListItem[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const loadNearbySpaces = useCallback(async (params: FetchNearbySpacesParams) => {
    setLoading(true)
    setIsError(false)
    setSpaces([])
    setTotalCount(0)

    try {
      const result = await fetchNearbySpaces(params)

      if (result.isOk) {
        setSpaces(result.data.spaces)
        setTotalCount(result.data.total_count)
      } else {
        setSpaces([])
        setTotalCount(0)
        setIsError(true)
        Sentry.captureException(new Error('fetchNearbySpaces returned isOk: false'), {
          tags: { hook: 'useNearbySpaces' },
          extra: {
            params,
            isNotFound: result.isNotFound,
          },
        })
      }
    } catch (error) {
      setSpaces([])
      setTotalCount(0)
      setIsError(true)
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { hook: 'useNearbySpaces' },
        extra: { params },
      })
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    spaces,
    totalCount,
    isLoading,
    isError,
    loadNearbySpaces,
  }
}
